import React, { useRef, useState, useEffect } from 'react';
import ReactAudioPlayer from 'react-audio-player';
import { useLocation, useSearchParams } from 'react-router-dom';
import { Col, Row } from 'react-bootstrap';
import { Range, Direction, getTrackBackground } from 'react-range';

import './App.scss';
import play from './assets/play.svg';
import playOnly from './assets/play-only.svg';
import pause from './assets/pause.svg';
import pauseOnly from './assets/pause-only.svg';
import info from './assets/info.png';

const App = () => {
  const playerRef = useRef();
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentVolume, setCurrentVolume] = useState(0.5);
  const [audioInformation, setAudioInformation] = useState({
    artist: "",
    title: "",
    album: "",
    imageurl: "",
  })
  const [radioInformation, setRadioInformation] = useState({
    audioFlux: "",
    color: "",
    pochetteFlux: "",
    title: "",
  })
  const [searchParams, setSearchParams] = useSearchParams();
  const [isRadioInfoLoaded, setIsRadioInfoLoaded] = useState(false);

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const token = params.get('token');

  // fetch radio informations
  const fetchRadioInfos = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API}/radios/validate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          radioToken: token
        })
      });
  
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
  
      const data = await response.json();
      console.log(data);
  
      setRadioInformation({
        audioFlux: data.radioData.audioFlux,
        color: data.radioData.color,
        pochetteFlux: data.radioData.pochetteFlux,
        title: data.radioData.title,
      });
  
      // Mark radio info as loaded
      setIsRadioInfoLoaded(true);
  
      // fetch audio information after radio information is fetched
      fetchAudioInformation(data.radioData.pochetteFlux);
  
    } catch (error) {
      console.error("An error occurred while fetching the radio information: ", error);
    }
  }

  const fetchAudioInformation = async (audioInfoURL) => {
    try {
      const response = await fetch(audioInfoURL);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log(data);

      setAudioInformation({
        artist: data.artist,
        title: data.title,
        album: data.album,
        imageurl: data.cover,
      });

    } catch (error) {
      console.error("An error occurred while fetching the audio information: ", error);
    }
  }

  useEffect(() => {
    fetchRadioInfos();
  
    const interval = setInterval(() => {
      // fetch audio information periodically only if radio information has been loaded
      if (isRadioInfoLoaded) {
        fetchAudioInformation(radioInformation.pochetteFlux);
      }
    }, 30000);
  
    return () => {
      clearInterval(interval);
    }
  }, [isRadioInfoLoaded]); // the effect depends on isRadioInfoLoaded

  return (
    <>
      <div className="App">
        <div>
          <ReactAudioPlayer
            ref={playerRef}
            src={radioInformation?.audioFlux ? radioInformation?.audioFlux : ""}
            autoPlay
            onPlay={() => setIsPlaying(true)}
            onPause={() => setIsPlaying(false)}
            volume={parseFloat(currentVolume)}
          />
        </div>
      </div>
      <div className='pod-player-wrapper'>
        <Row>
          <Col xl={12} lg={12} md={12} sm={12} xs={8}>
            <p className='pod-player__title'>RADIO <span style={{ ...(radioInformation?.color ? {color: radioInformation?.color} : {}) }}>{radioInformation?.title}</span></p>
          </Col>
          <Col
            xl={{ order: '2', span: 1 }}
            lg={{ order: '2', span: 2 }}
            md={{ order: '2', span: 2 }}
            sm={{ order: '2', span: 3 }}
            xs={{ order: 'first', span: 4 }}
          >
            <div className='pod-player__controls'>
              {isPlaying ? (
                <div className='player-button' style={{ padding: 8 }}>
                  <img
                    onClick={() => {
                      playerRef.current.audioEl.current.pause()
                    }}
                    style={{ backgroundColor: radioInformation?.color ? radioInformation?.color : '#8C549A', borderRadius: 100 }}
                    src={pauseOnly}
                    alt="pause"
                  />
                </div>
              ) : (
                <div className='player-button' style={{ padding: 8 }}>
                  <img
                    onClick={() => {
                      playerRef.current.audioEl.current.src = radioInformation?.audioFlux ? radioInformation?.audioFlux : '';
                      playerRef.current.audioEl.current.play()
                    }}
                    style={{ backgroundColor: radioInformation?.color ? radioInformation?.color : '#8C549A', borderRadius: 100 }}
                    src={playOnly}
                    alt="play"
                  />
                </div>
              )}
            </div>
          </Col>
          <Col
            xl={{ order: '3' }}
            lg={{ order: '3' }}
            md={{ order: '3' }}
            sm={{ order: '3' }}
          >
            <Row className='pod-player__details'>
              <Col xl="auto" lg={2} md={3} sm="auto" xs={4}>
                <div className='pod-player__thumbnail'>
                  <img src={audioInformation?.imageurl ? audioInformation?.imageurl : "podmedias-logo.jpg"} alt='thumbnail' />
                </div>
              </Col>
              <Col xl="auto" lg={1} md={1} sm={1} xs={1}>
                <div className='pod-player__volume'>
                  <Range
                    direction={Direction.Up}
                    values={[currentVolume]}
                    step={0.01}
                    min={0}
                    max={1}
                    onChange={(values) => setCurrentVolume(values[0])}
                    renderTrack={({ props, children }) => (
                      <div
                        onMouseDown={props.onMouseDown}
                        onTouchStart={props.onTouchStart}
                        style={{
                          ...props.style,
                          flexGrow: 1,
                          width: '8px',
                          display: 'flex',
                          height: '107px'
                        }}
                      >
                        <div
                          ref={props.ref}
                          style={{
                            width: '5px',
                            height: '100%',
                            borderRadius: '4px',
                            background: getTrackBackground({
                              values: [currentVolume],
                              colors: [radioInformation?.color ? radioInformation?.color : '#8C549A', '#ccc'],
                              min: 0,
                              max: 1,
                              direction: Direction.Up,
                            }),
                            alignSelf: 'center'
                          }}
                        >
                          {children}
                        </div>
                      </div>
                    )}
                    renderThumb={({ props, isDragged }) => (
                      <div
                        {...props}
                        style={{
                          ...props.style,
                          height: '20px',
                          width: '20px',
                          borderRadius: '10px',
                          backgroundColor: radioInformation?.color ? radioInformation?.color : '#8C549A',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                          boxShadow: '0px 2px 6px #AAA'
                        }}
                      />
                    )}
                  />
                </div>
              </Col>
              <Col xl="auto" lg={7} md={6} sm={5} xs={7}>
                <div className='pod-player__song-details'>
                  <p className='pod-player__artist'>{audioInformation.artist}</p>
                  <p className='pod-player__audio-title'>{audioInformation.title}</p>
                </div>
              </Col>
            </Row>
          </Col>
        </Row>
        <div className='pod-player__info'>
          <img src={info} alt="information" />
        </div>
      </div>
    </>
  );
}

export default App;
